/**
 * SaveButton Component
 * ====================
 * Reusable save/unsave button for materials.
 * Used in Explorer cards and Material Detail page.
 * 
 * Features:
 * - Clear visual feedback on save/unsave
 * - Smooth animations (scale, pulse, fill transition)
 * - Authentication check with redirect
 * - Multiple variants (icon, button, minimal)
 */

import React, { useState } from 'react';
import { Heart, Check } from 'lucide-react';
import { useBlueprint } from '../../context/BlueprintContext';
import { useMaterialSaveState } from '../../hooks/useSavedMaterials';

/**
 * SaveButton - Heart icon button for saving materials
 * @param {Object} props
 * @param {string} props.materialId - Material ID to save
 * @param {Object} props.materialData - Material metadata (name, image, type)
 * @param {string} props.variant - 'icon' | 'button' | 'minimal'
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional classes
 * @param {Function} props.onToggle - Callback after toggle (optional)
 * @param {boolean} props.showFeedback - Show brief success feedback (default: true)
 */
const SaveButton = ({ 
  materialId, 
  materialData = {}, 
  variant = 'icon',
  size = 'md',
  className = '',
  onToggle,
  showFeedback = true
}) => {
  const { getContent } = useBlueprint();
  const { isSaved, isLoading, toggle } = useMaterialSaveState(materialId, materialData);
  
  // Local state for animation feedback
  const [justSaved, setJustSaved] = useState(false);
  const [animating, setAnimating] = useState(false);
  
  // Size mappings
  const sizes = {
    sm: { icon: 14, padding: 'p-1.5' },
    md: { icon: 18, padding: 'p-2' },
    lg: { icon: 22, padding: 'p-2.5' }
  };
  
  const { icon: iconSize, padding } = sizes[size] || sizes.md;
  
  // Handle click with animation
  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // NO LOGIN REQUIRED - Demo mode uses localStorage
    // Start animation
    setAnimating(true);
    
    const result = await toggle();
    
    // Show success feedback
    if (result.success && showFeedback) {
      if (result.isSaved) {
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 1500);
      }
    }
    
    // End animation
    setTimeout(() => setAnimating(false), 300);
    
    if (onToggle) {
      onToggle(result);
    }
  };
  
  // Labels from Blueprint
  const saveLabel = getContent('actions.save', 'Salva');
  const savedLabel = getContent('actions.saved', 'Salvato');
  const savingLabel = getContent('actions.saving', 'Salvataggio...');
  
  // Animation classes
  const animationClass = animating ? 'scale-125' : 'scale-100';
  const pulseClass = justSaved ? 'animate-pulse' : '';
  
  // Render based on variant
  if (variant === 'minimal') {
    return (
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`transition-all duration-200 ${animationClass} ${className}`}
        title={isSaved ? savedLabel : saveLabel}
        data-testid="save-button-minimal"
      >
        <Heart
          size={iconSize}
          className={`transition-all duration-300 ${pulseClass} ${
            isSaved 
              ? 'fill-[var(--gold)] text-[var(--gold)]' 
              : 'text-[#888] hover:text-[var(--gold)]'
          } ${isLoading ? 'opacity-50' : ''}`}
        />
      </button>
    );
  }
  
  if (variant === 'button') {
    return (
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm 
          transition-all duration-300 ${animationClass}
          ${isSaved 
            ? 'bg-[var(--gold)] text-white hover:bg-[var(--gold-lt)]' 
            : 'bg-[#1A1A18] text-white border border-[#3A3A38] hover:border-[var(--gold)]'
          }
          ${isLoading ? 'opacity-50 cursor-wait' : ''}
          ${className}
        `}
        data-testid="save-button"
      >
        {/* Icon with transition */}
        <span className={`transition-transform duration-300 ${animating ? 'scale-110' : ''}`}>
          {justSaved ? (
            <Check size={iconSize} className="text-white" />
          ) : (
            <Heart
              size={iconSize}
              className={`transition-all duration-300 ${isSaved ? 'fill-white' : ''}`}
            />
          )}
        </span>
        
        {/* Label */}
        <span className="transition-opacity duration-200">
          {isLoading ? savingLabel : (justSaved ? 'Salvato!' : (isSaved ? savedLabel : saveLabel))}
        </span>
      </button>
    );
  }
  
  // Default: icon variant (circle button)
  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`
        ${padding} rounded-full transition-all duration-300 ${animationClass}
        ${isSaved 
          ? 'bg-[var(--gold)] text-white shadow-lg shadow-[var(--gold)]/30' 
          : 'bg-black/40 backdrop-blur-sm text-white hover:bg-black/60'
        }
        ${isLoading ? 'opacity-50 cursor-wait' : ''}
        ${justSaved ? 'ring-2 ring-[var(--gold)] ring-offset-2 ring-offset-transparent' : ''}
        ${className}
      `}
      title={isSaved ? savedLabel : saveLabel}
      data-testid="save-button-icon"
    >
      <Heart
        size={iconSize}
        className={`transition-all duration-300 ${pulseClass} ${isSaved ? 'fill-white' : ''}`}
      />
    </button>
  );
};

export default SaveButton;
